import {
  type Block,
  Deck,
  type GenerationOptions,
  type StudioStep,
  UNPLACED_PIN_Y,
  flattenDeck,
} from "@hali-shares/deck-schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, type Asset, type DeckRecord, type DeckSettings, api, issuesOf } from "../../lib/api";
import { APP_NAME } from "../../lib/brand";
import { readStore, removeStore } from "../../lib/storage";
import {
  BlockDrawer,
  type Commit,
  DeckDrawer,
  PreflightDrawer,
  ReferenceDrawer,
  SectionDrawer,
  SessionsDrawer,
  VersionsDrawer,
} from "./drawers";
import { isBlankDeck, isEmptySection, minutesTotal } from "./model";
import { type Finding, type Target, checkDeck, checkPins } from "./preflight";
import { SignOutButton } from "./SignOut";
import { SlidesGrid } from "./SlidesGrid";
import { BasicsStep } from "./steps/BasicsStep";
import type { AiError, Settings, StudioCtx } from "./steps/ctx";
import { OutlineStep } from "./steps/OutlineStep";
import { ReviewStep } from "./steps/ReviewStep";
import { SlidesStep } from "./steps/SlidesStep";
import { Stepper } from "./steps/Stepper";
import "./studio.css";
import "./steps/steps.css";

type Panel =
  | { kind: "block"; blockIndex: number | null; block: Block }
  | { kind: "section"; index: number | null }
  | { kind: "reference"; refId: string | null }
  | { kind: "deck" }
  | { kind: "versions" }
  | { kind: "sessions" }
  | { kind: "preflight" }
  | { kind: "slides" }
  | null;

/** Where this browser kept generation settings before they belonged to the talk; copied into a talk once. */
const LEGACY_OPTIONS_KEY = "hali-shares:generation-options";

type Status = { kind: "saved" } | { kind: "saving" } | { kind: "error"; message: string };

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

export function StudioPage() {
  const { deckId = "" } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState<DeckRecord | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [source, setSource] = useState("");
  const [selected, setSelected] = useState(0);
  const [panel, setPanel] = useState<Panel>(null);
  const [status, setStatus] = useState<Status>({ kind: "saved" });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [gen, setGen] = useState<{ configured: boolean; provider: string; vision: boolean } | null>(null);
  const [busy, setBusy] = useState<StudioCtx["busy"]>(null);
  const [aiError, setAiError] = useState<AiError | null>(null);
  const [placing, setPlacing] = useState(false);
  // The version to go back to after an AI change, until anything else changes the deck.
  const [undo, setUndo] = useState<{ version: number; label: string } | null>(null);
  const [pinFindings, setPinFindings] = useState<Finding[]>([]);
  // The settings as last written, so quick successive saves (a step change right after a brief edit) build on
  // each other instead of on the render they started from.
  const settingsRef = useRef<Settings>(withDefaults(undefined));

  useEffect(() => {
    api.deck(deckId).then(
      (r) => {
        const settings = withDefaults(r.settings);
        const legacy = readStore<GenerationOptions | null>(LEGACY_OPTIONS_KEY, null);
        if (Object.keys(settings.generation).length === 0 && legacy && Object.keys(legacy).length > 0) {
          // Settings from before they were the talk's: give them to this talk, then forget the browser copy.
          settings.generation = legacy;
          api.saveSettings(deckId, settings).then(
            () => removeStore(LEGACY_OPTIONS_KEY),
            () => {},
          );
        }
        settingsRef.current = settings;
        setRecord({ ...r, settings });
        setSource(r.source);
      },
      (e: unknown) => {
        if (e instanceof ApiError && e.status === 401) navigate(`/login?next=/studio/${deckId}`, { replace: true });
        else setLoadError(errorText(e));
      },
    );
    api.assets(deckId).then(setAssets, () => {});
    api.generationStatus().then(setGen, () => {});
  }, [deckId, navigate]);

  // Notes autosave (no version: they aren't the deck).
  const savedSource = useRef<string | null>(null);
  useEffect(() => {
    if (!record) return;
    if (savedSource.current === null) savedSource.current = record.source;
    if (source === savedSource.current) return;
    const t = window.setTimeout(() => {
      api.saveSource(deckId, source).then(
        () => (savedSource.current = source),
        () => {},
      );
    }, 800);
    return () => window.clearTimeout(t);
  }, [source, record, deckId]);

  // Responses can arrive out of order (a pin placement finishing after a later save): never go back a version.
  const accept = useCallback((r: DeckRecord) => {
    setRecord((cur) =>
      cur && cur.id === r.id && r.version < cur.version ? cur : { ...r, settings: settingsRef.current },
    );
    setSelected((s) => Math.min(s, r.spec.sections.length - 1));
    setStatus({ kind: "saved" });
    setUndo(null);
  }, []);

  const saveSettings = useCallback(
    (patch: Partial<Settings>) => {
      const next = { ...settingsRef.current, ...patch };
      settingsRef.current = next;
      setRecord((r) => (r ? { ...r, settings: next } : r));
      api
        .saveSettings(deckId, next)
        .catch((e: unknown) => setStatus({ kind: "error", message: `Settings not saved: ${errorText(e)}` }));
    },
    [deckId],
  );

  // Pins on blank space need the images, so they're checked in the background whenever the deck changes.
  const spec = record?.spec;
  useEffect(() => {
    if (!spec) return;
    let live = true;
    const t = window.setTimeout(() => {
      checkPins(spec).then(
        (f) => live && setPinFindings(f),
        () => {},
      );
    }, 400);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [spec]);
  const findings = useMemo(() => (spec ? [...checkDeck(spec), ...pinFindings] : []), [spec, pinFindings]);

  const version = useRef(0); // the version on screen, for saves to name as their base
  useEffect(() => {
    if (record) version.current = record.version;
  }, [record]);

  const commit: Commit = useCallback(
    async (spec, note) => {
      const checked = Deck.safeParse(spec);
      if (!checked.success) return checked.error.issues.map((i) => ({ path: i.path.join("."), message: i.message }));
      setStatus({ kind: "saving" });
      try {
        accept(await api.updateDeck(deckId, checked.data, note, version.current));
        return [];
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          // Changed elsewhere (another tab, pins placed): show the newer deck; the open editor keeps the edit.
          const latest = await api.deck(deckId).catch(() => null);
          if (latest) accept(latest);
          setStatus({ kind: "error", message: errorText(e) });
          return [{ path: "", message: errorText(e) }];
        }
        setStatus({ kind: "error", message: errorText(e) });
        const issues = issuesOf(e);
        return issues.length ? issues : [{ path: "", message: errorText(e) }];
      }
    },
    [deckId, accept],
  );

  const upload = useCallback(
    async (file: File): Promise<Asset | null> => {
      try {
        const a = await api.uploadAsset(file, deckId);
        setAssets((list) => [a, ...list]);
        return a;
      } catch (e) {
        setStatus({ kind: "error", message: `Upload failed: ${errorText(e)}` });
        return null;
      }
    },
    [deckId],
  );

  if (loadError) return <p style={{ padding: 32 }}>Could not open this deck: {loadError}</p>;
  if (!record) return null;
  const deck = record.spec;
  const settings = withDefaults(record.settings);
  const step: StudioStep = settings.step ?? (isBlankDeck(deck) ? "basics" : "slides");
  const sectionIndex = Math.min(selected, deck.sections.length - 1);

  /**
   * After a generation: move pins still on the middle line onto what their questions are about. The deck is
   * already usable meanwhile; anything the presenter moves first is left alone.
   */
  const placePins = async (r: DeckRecord, keepUndo: { version: number; label: string } | null) => {
    const pending = r.spec.sections.some((s) =>
      s.blocks.some((b) => b.type === "screenshot" && b.assetId && b.pins.some((p) => p.y === UNPLACED_PIN_Y)),
    );
    if (!gen?.vision || !pending) return;
    setPlacing(true);
    try {
      const placed = await api.placePins(deckId);
      if (placed.version > r.version) {
        accept(placed); // ignored if something newer already arrived
        setUndo(keepUndo);
      }
    } catch {
      /* the pre-live check still flags pins on blank space */
    }
    setPlacing(false);
  };

  const failed = (where: AiError["where"], e: unknown) =>
    setAiError({ where, message: errorText(e), issues: issuesOf(e) });

  const generateAll = async (followOutline: boolean) => {
    setBusy("deck");
    setAiError(null);
    const before = record.version;
    const blank = isBlankDeck(deck);
    try {
      const r = await api.generate(deckId, source, settings.brief.minutes, settings.generation, followOutline);
      savedSource.current = source;
      accept(r);
      setSelected(0);
      const next = blank ? null : { version: before, label: "Every slide drafted from your outline and notes." };
      setUndo(next);
      setBusy(null);
      void placePins(r, next);
      return true;
    } catch (e) {
      failed("deck", e);
    }
    setBusy(null);
    return false;
  };

  const draftSection = async (si: number, instruction: string) => {
    const s = deck.sections[si]!;
    setBusy(`section:${s.id}`);
    setAiError(null);
    const before = record.version;
    try {
      const r = await api.regenerateSection(deckId, s.id, instruction);
      accept(r);
      const next = { version: before, label: `Section ${si + 1} ${isEmptySection(s) ? "drafted" : "redrafted"}.` };
      setUndo(next);
      // A redraft is new content: it needs another look.
      if (settingsRef.current.plan[s.id]?.reviewed)
        saveSettings({
          plan: { ...settingsRef.current.plan, [s.id]: { ...settingsRef.current.plan[s.id], reviewed: false } },
        });
      setBusy(null);
      void placePins(r, next);
      return true;
    } catch (e) {
      failed(`section:${s.id}`, e);
    }
    setBusy(null);
    return false;
  };

  const undoAi = async () => {
    if (!undo) return;
    setStatus({ kind: "saving" });
    try {
      accept(await api.restore(deckId, undo.version));
    } catch (e) {
      setStatus({ kind: "error", message: errorText(e) });
    }
  };

  const toFix = findings.filter((f) => f.level === "fix").length;
  const openTarget = (t: Target) => {
    if (t.kind === "block") {
      setSelected(t.sectionIndex);
      const b = deck.sections[t.sectionIndex]?.blocks[t.blockIndex];
      if (b) setPanel({ kind: "block", blockIndex: t.blockIndex, block: b });
    } else if (t.kind === "section") {
      setSelected(t.sectionIndex);
      setPanel({ kind: "section", index: t.sectionIndex });
    } else if (t.kind === "reference") setPanel({ kind: "reference", refId: t.refId });
    else if (t.kind === "deck") setPanel({ kind: "deck" });
  };

  const ctx: StudioCtx = {
    deckId,
    record,
    deck,
    settings,
    saveSettings,
    commit,
    ai: gen,
    source,
    setSource,
    assets,
    setAssets,
    upload,
    setError: (message) => setStatus({ kind: "error", message }),
    selected: sectionIndex,
    setSelected,
    goto: (s) => {
      if (s !== step) saveSettings({ step: s });
      window.scrollTo({ top: 0 });
    },
    findings,
    openTarget,
    openBlock: (si, bi, block) => {
      setSelected(si);
      setPanel({ kind: "block", blockIndex: bi, block });
    },
    openSection: (si) => setPanel({ kind: "section", index: si }),
    openReference: (refId) => setPanel({ kind: "reference", refId }),
    generateAll,
    draftSection,
    busy,
    aiError,
  };

  const statusText = status.kind === "saving" ? "saving…" : status.kind === "error" ? "not saved" : "saved";

  return (
    <div className="studio">
      <header className="studio-bar">
        <div className="studio-crumbs">
          <Link to="/studio" className="studio-wordmark">
            {APP_NAME}
          </Link>
          <span className="studio-sep">/</span>
          <button
            type="button"
            className="studio-title"
            onClick={() => setPanel({ kind: "deck" })}
            title="Edit talk details"
          >
            {deck.title}
          </button>
          <button
            type="button"
            className="studio-title studio-status"
            onClick={() => setPanel({ kind: "versions" })}
            title="Versions"
          >
            v{record.version} · {statusText}
          </button>
        </div>
        <div className="studio-actions">
          <SignOutButton />
          <button type="button" className="s-btn" onClick={() => setPanel({ kind: "sessions" })}>
            Sessions
          </button>
          <button type="button" className="s-btn" onClick={() => setPanel({ kind: "versions" })}>
            Versions
          </button>
          <button type="button" className="s-btn" onClick={() => setPanel({ kind: "slides" })}>
            All slides
          </button>
          <a className="s-btn" href={`/present/${deckId}?rehearse=1`} target="_blank" rel="noopener">
            Rehearse
          </a>
          {findings.length > 0 ? (
            <button type="button" className="s-btn s-btn-yellow" onClick={() => setPanel({ kind: "preflight" })}>
              Start live session
              {toFix > 0 && <span className="count-badge">{toFix} to fix</span>}
            </button>
          ) : (
            <a className="s-btn s-btn-yellow" href={`/present/${deckId}`} target="_blank" rel="noopener">
              Start live session
            </a>
          )}
        </div>
      </header>
      <Stepper ctx={ctx} current={step} />
      {status.kind === "error" && (
        <p className="banner banner-yellow studio-banner" role="alert">
          {status.message}
        </p>
      )}
      {record.archivedAt && (
        <p className="banner banner-grey undo-bar studio-banner" role="status">
          This talk is archived: it isn’t in your list, but nothing about it is lost.
          <button
            type="button"
            className="s-btn s-btn-small"
            onClick={() =>
              api.unarchiveDeck(deckId).then(
                (r) => setRecord({ ...r, settings: settingsRef.current }),
                (e: unknown) => setStatus({ kind: "error", message: errorText(e) }),
              )
            }
          >
            Restore
          </button>
        </p>
      )}
      {undo && (
        <p className="banner banner-grey undo-bar studio-banner" role="status">
          {undo.label} Every version is kept.
          <button type="button" className="s-btn s-btn-small" onClick={undoAi}>
            Undo
          </button>
          <button type="button" className="link-btn" onClick={() => setUndo(null)}>
            Keep
          </button>
        </p>
      )}
      {placing && (
        <p className="busy studio-banner" role="status">
          PLACING PINS ON YOUR SCREENSHOTS · THE DECK IS READY TO EDIT
        </p>
      )}

      <main className="studio-step">
        {step === "basics" && <BasicsStep ctx={ctx} />}
        {step === "outline" && <OutlineStep ctx={ctx} />}
        {step === "slides" && <SlidesStep ctx={ctx} key={deck.sections[sectionIndex]!.id} />}
        {step === "review" && <ReviewStep ctx={ctx} />}
      </main>

      {panel?.kind === "block" && (
        <BlockDrawer
          key={`${sectionIndex}:${panel.blockIndex}:${panel.block.id}`}
          deckId={deckId}
          aiEnabled={gen?.configured !== false}
          deck={deck}
          sectionIndex={sectionIndex}
          blockIndex={panel.blockIndex}
          initial={panel.block}
          assets={assets}
          upload={upload}
          commit={commit}
          onClose={() => setPanel(null)}
        />
      )}
      {panel?.kind === "section" && (
        <SectionDrawer
          deck={deck}
          sectionIndex={panel.index}
          commit={async (spec, note) => {
            const issues = await commit(spec, note);
            if (!issues.length && panel.index === null) setSelected(spec.sections.length - 1);
            return issues;
          }}
          onClose={() => setPanel(null)}
          onDeleted={() => {
            setSelected(0);
            setPanel(null);
          }}
        />
      )}
      {panel?.kind === "reference" && (
        <ReferenceDrawer
          deck={deck}
          sectionIndex={sectionIndex}
          refId={panel.refId}
          commit={commit}
          onClose={() => setPanel(null)}
        />
      )}
      {panel?.kind === "deck" && <DeckDrawer deck={deck} commit={commit} onClose={() => setPanel(null)} />}
      {panel?.kind === "sessions" && <SessionsDrawer deckId={deckId} onClose={() => setPanel(null)} />}
      {panel?.kind === "preflight" && (
        <PreflightDrawer deckId={deckId} findings={findings} onOpen={openTarget} onClose={() => setPanel(null)} />
      )}
      {panel?.kind === "slides" && <AllSlides deck={deck} onOpen={openTarget} onClose={() => setPanel(null)} />}
      {panel?.kind === "versions" && (
        <VersionsDrawer
          deckId={deckId}
          current={record.version}
          onRestored={(r) => {
            accept(r);
            setPanel(null);
          }}
          onClose={() => setPanel(null)}
        />
      )}
    </div>
  );
}

/** The talk's settings with every part present (older talks stored only `generation`). */
function withDefaults(s: DeckSettings | undefined): Settings {
  return { ...s, generation: s?.generation ?? {}, brief: s?.brief ?? {}, plan: s?.plan ?? {} };
}

/** Every slide of the talk at a size you can read; click one to edit it. */
function AllSlides({ deck, onOpen, onClose }: { deck: Deck; onOpen: (t: Target) => void; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="all-slides" role="dialog" aria-modal="true" aria-label="All slides">
      <header className="all-slides-head">
        <h2 className="drawer-title">All slides</h2>
        <span className="col-num">
          {flattenDeck(deck).length} slides · {minutesTotal(deck)} min
        </span>
        <span className="spacer" />
        <button type="button" className="s-btn" onClick={onClose}>
          Close
        </button>
      </header>
      <SlidesGrid deck={deck} onOpen={onOpen} />
    </div>
  );
}
